import { type ReactElement, type ReactNode, useId } from 'react';
import { cn } from '@fshr/shared-react';
import BevSafeText from '../bev-safe-text';
import Icon from '../icon';

export type AccordionItemProps = {
	question: string;
	answer: ReactNode;
	isExpanded: boolean;
	isFirst?: boolean;
	onToggle: () => void;
};

export default function AccordionItem({
	question,
	answer,
	isExpanded,
	isFirst,
	onToggle,
}: AccordionItemProps): ReactElement {
	const id = useId();
	const buttonId = `accordion-btn-${id}`;
	const panelId = `accordion-panel-${id}`;

	return (
		<div
			className={cn(
				'border-b border-border-subtle pb-card-stack-s md:pb-stack-l',
				isFirst && 'border-t'
			)}
		>
			<h3 className="text-h3 text-text-heading">
				<button
					id={buttonId}
					type="button"
					className="flex w-full cursor-pointer items-center justify-between gap-x-inline-m pt-card-padding-s text-left"
					onClick={onToggle}
					aria-expanded={isExpanded}
					aria-controls={panelId}
				>
					<span className="min-w-0">{question}</span>
					<span className="shrink-0" aria-hidden="true">
						<Icon
							icon={isExpanded ? 'less_sign' : 'plus_sign'}
							className="text-[12px] text-text-heading"
						/>
					</span>
				</button>
			</h3>
			<div
				id={panelId}
				role="region"
				aria-labelledby={buttonId}
				className={cn(
					'grid transition-[grid-template-rows] duration-300 ease-in-out',
					isExpanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
				)}
			>
				<div className="overflow-hidden">
					<div className="pt-card-padding-s">
						<div className="text-body2 text-text-primary py-200 text-left [&>*]:ml-0 [&>*]:pl-0">
							<BevSafeText>{answer}</BevSafeText>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}
